import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { ShieldAlert, Loader2, RefreshCw, AlertCircle, AlertTriangle, Info, CheckCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const SEVERITY = {
  critical: { cls: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500", icon: AlertCircle },
  warning: { cls: "bg-yellow-100 text-yellow-700 border-yellow-200", dot: "bg-yellow-400", icon: AlertTriangle },
  info: { cls: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-400", icon: Info },
};

const EVENT_LABELS = {
  login: "Login", logout: "Logout", failed_login: "Failed Login",
  card_added: "Card Added", card_deleted: "Card Deleted", token_accessed: "Token Accessed",
  key_created: "Key Created", key_revoked: "Key Revoked", settings_changed: "Settings Changed",
  unauthorized_access: "Unauthorized Access",
};

export default function SecurityCenter() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    base44.entities.SecurityEvent.list("-created_date", 200).then(setEvents).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resolve = async (event) => {
    await base44.entities.SecurityEvent.update(event.id, { resolved: true });
    setEvents((prev) => prev.map((e) => e.id === event.id ? { ...e, resolved: true } : e));
  };

  const filtered = filter === "all" ? events : filter === "unresolved" ? events.filter((e) => !e.resolved) : events.filter((e) => e.severity === filter);

  const counts = {
    critical: events.filter((e) => e.severity === "critical" && !e.resolved).length,
    warning: events.filter((e) => e.severity === "warning" && !e.resolved).length,
    info: events.filter((e) => e.severity === "info" && !e.resolved).length,
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Security Center</h1>
          <p className="text-sm text-slate-500 mt-0.5">Flagged incidents and security events</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {Object.entries(counts).map(([sev, count]) => {
          const s = SEVERITY[sev];
          const Icon = s.icon;
          return (
            <div key={sev} className={`rounded-2xl border p-4 flex items-center gap-3 ${s.cls}`}>
              <Icon className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-2xl font-semibold">{count}</p>
                <p className="text-xs capitalize font-medium">{sev} open</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 mb-5 w-fit">
        {["all", "critical", "warning", "info", "unresolved"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${filter === f ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <ShieldAlert className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>No events match this filter.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {filtered.map((event) => {
            const s = SEVERITY[event.severity] || SEVERITY.info;
            const Icon = s.icon;
            return (
              <div key={event.id} className={`flex items-start gap-4 px-5 py-4 ${event.resolved ? "opacity-50" : ""}`}>
                <div className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${s.cls}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-900">{EVENT_LABELS[event.event_type] || event.event_type?.replace(/_/g, " ")}</span>
                    <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 border ${s.cls}`}>{event.severity}</span>
                    {event.resolved && <span className="text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-slate-100 text-slate-500">Resolved</span>}
                  </div>
                  <p className="text-sm text-slate-600">{event.description || "No description provided."}</p>
                  {event.ip_address && <p className="text-xs text-slate-400 mt-0.5">Source IP: {event.ip_address}</p>}
                </div>
                <div className="flex-shrink-0 text-right space-y-1">
                  <p className="text-xs text-slate-400">{event.created_date ? formatDistanceToNow(new Date(event.created_date), { addSuffix: true }) : "—"}</p>
                  {!event.resolved && (
                    <button onClick={() => resolve(event)}
                      className="inline-flex items-center gap-1 text-xs rounded-lg border border-emerald-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50 transition">
                      <CheckCircle className="h-3 w-3" /> Resolve
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}